"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupProfileRequestSchema = exports.verifySignUpRequestSchema = exports.SIGN_UP_CODE_LENGTH = exports.signUpRequestSchema = exports.setupStateSchema = exports.setupWorkspaceResponseSchema = exports.setupCurrencyRequestSchema = exports.setupWorkspaceRequestSchema = exports.setupCurrencySchema = void 0;
const zod_1 = require("zod");
const contract_1 = require("../workspace/contract");
const currency_1 = require("../workspace/currency");
/**
 * ⚠️ `decisions.md` U-13 · **the currency step 2 offers is one of `CURRENCY_CATALOGUE`'s**, the
 * list the settings dialog already offers. The server writes the catalogue's name and symbol, so a
 * code is all the client sends — and a code off the list is a validation failure rather than a
 * currency with no name.
 */
exports.setupCurrencySchema = zod_1.z
    .string()
    .refine((code) => currency_1.CURRENCY_CATALOGUE.some((entry) => entry.code === code), {
    message: 'not a currency the setup flow offers',
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The first-run setup flow — `0202`, and the onboarding mockup.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Four steps: profile, workspace, invite, all set. Step 1 happens where the account is made — the
 * invitation page for an invited admin or colleague — so these are the endpoints for steps 2 and
 * 4, which run signed in **with no workspace**, and the state `/auth/me` reports for resuming.
 *
 * ⚠️⚠️ **THE ADDRESS IS REQUIRED HERE, WHERE `createWorkspaceRequestSchema` MAKES IT OPTIONAL.**
 * A second workspace is named in a wizard that does not ask for an address, so the server derives
 * and suffixes one. Step 2 asks, beside a live check — so the address is the person's choice, and
 * `0202` refuses a collision rather than quietly handing them `delivery-2`.
 */
exports.setupWorkspaceRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(80),
    slug: zod_1.z.string().trim().regex(contract_1.WORKSPACE_SLUG),
    timezone: zod_1.z.enum(contract_1.WORKSPACE_TIMEZONES),
    /** ⚠️ `0214` · the workspace's default currency, created with it (U-13). */
    currency: exports.setupCurrencySchema,
});
/**
 * ⚠️⚠️ U-13 · **STEP 2 REOPENED CHANGES THE DEFAULT CURRENCY HERE, NOT THROUGH `PATCH /workspace`.**
 * `PUT /setup/currency`. Only while setup is unfinished and the workspace's money is the demo's
 * alone — the demo's amounts are converted with it. Afterwards a default currency is a settings
 * matter, where re-basing the rates is the person's decision.
 */
exports.setupCurrencyRequestSchema = zod_1.z.object({ currency: exports.setupCurrencySchema });
exports.setupWorkspaceResponseSchema = zod_1.z.object({
    workspaceId: zod_1.z.uuid(),
    workspaceSlug: zod_1.z.string(),
    memberId: zod_1.z.uuid(),
});
/**
 * What `/auth/me` says about setup.
 *
 * ⚠️ **The step is the client's to work out, from two facts.** No workspace yet: step 2, with the
 * organisation's name to prefill when an operator invited this person. A workspace and `finished`
 * false: step 3, in that workspace. Anything else: the product. A step number on the wire would be
 * a third fact that could disagree with the first two.
 */
exports.setupStateSchema = zod_1.z.object({
    finished: zod_1.z.boolean(),
    organisationName: zod_1.z.string().nullable(),
    /**
     * ⚠️ `0203` · **whether step 1 has made the account row.** `false` only for somebody flow A just
     * signed in with an emailed code — no name and no password of their own yet — so the client
     * shows step 1 before step 2.
     */
    profileDone: zod_1.z.boolean(),
});
/**
 * Self sign-up, flow A (`0203`): an address, then the code mailed to it.
 *
 * ⚠️ **No password on the first screen.** The person chooses one in setup step 1; signup is given a
 * random one nobody sees, replaced there.
 */
exports.signUpRequestSchema = zod_1.z.object({
    email: zod_1.z.email().max(320),
});
exports.SIGN_UP_CODE_LENGTH = 6;
exports.verifySignUpRequestSchema = zod_1.z.object({
    email: zod_1.z.email().max(320),
    code: zod_1.z.string().regex(/^\d{6}$/),
});
/** Setup step 1 for a self-serve account: the name, and the password chosen at last. */
exports.setupProfileRequestSchema = zod_1.z.object({
    familyName: zod_1.z.string().trim().min(1).max(100),
    givenName: zod_1.z.string().trim().min(1).max(100),
    password: zod_1.z.string().min(1).max(200),
});
//# sourceMappingURL=setup.js.map